import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, Chalkboard, BookOpen, PencilSimple } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

export default function Classes() {
  const { user } = useAuth();
  const canEdit = user && (
    ["super_admin","owner","admin","director","study_director","secretary"].includes(user.role) ||
    (user.permissions || []).includes("academic")
  );
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [years, setYears] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [openClass, setOpenClass] = useState(false);
  const [openSubject, setOpenSubject] = useState(false);
  const [editingClass, setEditingClass] = useState(null);   // classe en cours d'édition
  const [editingSubject, setEditingSubject] = useState(null);
  const [clsForm, setClsForm] = useState({ name: "", level: "Primaire", series: "", capacity: 40, grade_level_id: "", academic_year_id: "", room: "", stream: "", main_teacher_id: "" });
  const [subForm, setSubForm] = useState({ name: "", code: "", coefficient: 1, class_id: "" });

  const [showArchived, setShowArchived] = useState(false);
  const load = async () => {
    try {
      const params = showArchived ? "?include_archived=true" : "";
      const [c, s, g, y, t] = await Promise.all([
        api.get(`/classes${params}`),
        api.get(`/subjects${params}`),
        api.get("/grade-levels"),
        api.get("/academic-years"),
        api.get("/teachers").catch(() => ({ data: [] })),
      ]);
      setClasses(c.data); setSubjects(s.data); setGradeLevels(g.data); setYears(y.data); setTeachers(t.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  const currentYear = years.find(y => y.is_current) || years[0];

  const saveClass = async () => {
    try {
      const payload = { ...clsForm, academic_year_id: clsForm.academic_year_id || currentYear?.id || null };
      if (!payload.grade_level_id) delete payload.grade_level_id;
      if (!payload.academic_year_id) delete payload.academic_year_id;
      if (!payload.main_teacher_id) delete payload.main_teacher_id;
      if (editingClass) {
        await api.patch(`/classes/${editingClass.id}`, payload);
        toast.success("Classe modifiée");
      } else {
        await api.post("/classes", payload);
        toast.success("Classe créée");
      }
      setOpenClass(false); setEditingClass(null);
      setClsForm({ name: "", level: "Primaire", series: "", capacity: 40, grade_level_id: "", academic_year_id: "", room: "", stream: "", main_teacher_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openEditClass = (c) => {
    setEditingClass(c);
    setClsForm({
      name: c.name || "", level: c.level || "Primaire", series: c.series || "",
      capacity: c.capacity || 40, grade_level_id: c.grade_level_id || "",
      academic_year_id: c.academic_year_id || "", room: c.room || "", stream: c.stream || "",
      main_teacher_id: c.main_teacher_id || c.class_teacher_id || "",
    });
    setOpenClass(true);
  };
  const saveSubject = async () => {
    try {
      const payload = { ...subForm, coefficient: Number(subForm.coefficient) };
      if (payload.class_id === "none" || !payload.class_id) delete payload.class_id;
      if (editingSubject) {
        await api.patch(`/subjects/${editingSubject.id}`, payload);
        toast.success("Matière modifiée");
      } else {
        await api.post("/subjects", payload);
        toast.success("Matière créée");
      }
      setOpenSubject(false); setEditingSubject(null);
      setSubForm({ name: "", code: "", coefficient: 1, class_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openEditSubject = (s) => {
    setEditingSubject(s);
    setSubForm({ name: s.name || "", code: s.code || "", coefficient: s.coefficient ?? 1, class_id: s.class_id || "" });
    setOpenSubject(true);
  };
  const delClass = async (id) => {
    if (!window.confirm("Supprimer cette classe ? Si elle contient des élèves ou notes, elle sera archivée (soft-delete).")) return;
    try {
      const r = await api.delete(`/classes/${id}`);
      if (r.data?.archived) toast.info(`Classe archivée (${r.data.linked_records} enregistrements liés préservés)`);
      else toast.success("Classe supprimée");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const restoreClass = async (id) => {
    try { await api.post(`/classes/${id}/restore`); toast.success("Classe restaurée"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const delSubject = async (id) => {
    if (!window.confirm("Supprimer cette matière ? Si elle est utilisée, elle sera archivée.")) return;
    try {
      const r = await api.delete(`/subjects/${id}`);
      if (r.data?.archived) toast.info(`Matière archivée (${r.data.linked_records} enregistrements liés)`);
      else toast.success("Matière supprimée");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const restoreSubject = async (id) => {
    try { await api.post(`/subjects/${id}/restore`); toast.success("Matière restaurée"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const classById = Object.fromEntries(classes.map(c => [c.id, c]));

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Structure pédagogique</div>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight">Classes & Matières</h1>
      </header>

      <div className="mb-4">
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-muted-foreground">
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)}
                 data-testid="toggle-archived" />
          Inclure les archivés
        </label>
      </div>

      <Tabs defaultValue="classes">
        <TabsList data-testid="tabs-structure">
          <TabsTrigger value="classes" data-testid="tab-classes">Classes ({classes.length})</TabsTrigger>
          <TabsTrigger value="subjects" data-testid="tab-subjects">Matières ({subjects.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="classes" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={openClass} onOpenChange={(v) => { setOpenClass(v); if (!v) setEditingClass(null); }}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-class"
                        onClick={() => { setEditingClass(null); setClsForm({ name: "", level: "Primaire", series: "", capacity: 40, grade_level_id: "", academic_year_id: "", room: "", stream: "", main_teacher_id: "" }); }}>
                  <Plus size={16} className="mr-2" /> Nouvelle classe
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{editingClass ? `Modifier ${editingClass.name}` : "Nouvelle classe"}</DialogTitle></DialogHeader>
                <div className="grid gap-4">
                  <div><Label>Nom (ex: CE1, 6ème A, Terminale D)</Label><Input value={clsForm.name} onChange={e => setClsForm({ ...clsForm, name: e.target.value })} data-testid="input-class-name" /></div>
                  <div>
                    <Label>Niveau académique (neutre)</Label>
                    <Select value={clsForm.grade_level_id || "none"} onValueChange={v => setClsForm({ ...clsForm, grade_level_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="select-class-grade-level"><SelectValue placeholder="Choisir un niveau…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun</SelectItem>
                        {gradeLevels.map(g => <SelectItem key={g.id} value={g.id}>{g.name}{g.code ? ` (${g.code})` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground mt-1">Recommandé — permet grades scale et parcours internationaux.</p>
                  </div>
                  <div>
                    <Label>Étape (legacy)</Label>
                    <Select value={clsForm.level} onValueChange={v => setClsForm({ ...clsForm, level: v })}>
                      <SelectTrigger data-testid="select-class-level"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Maternelle">Maternelle</SelectItem>
                        <SelectItem value="Primaire">Primaire</SelectItem>
                        <SelectItem value="Collège">Collège</SelectItem>
                        <SelectItem value="Lycée">Lycée</SelectItem>
                        <SelectItem value="Formation">Centre de formation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Série / Stream (optionnel)</Label><Input value={clsForm.series} onChange={e => setClsForm({ ...clsForm, series: e.target.value })} data-testid="input-class-series" /></div>
                    <div><Label>Salle</Label><Input value={clsForm.room} onChange={e => setClsForm({ ...clsForm, room: e.target.value })} data-testid="input-class-room" /></div>
                  </div>
                  <div><Label>Capacité</Label><Input type="number" value={clsForm.capacity} onChange={e => setClsForm({ ...clsForm, capacity: Number(e.target.value) })} data-testid="input-class-capacity" /></div>
                  <div>
                    <Label>Titulaire (professeur principal)</Label>
                    <Select value={clsForm.main_teacher_id || "none"} onValueChange={v => setClsForm({ ...clsForm, main_teacher_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="select-class-main-teacher"><SelectValue placeholder="Aucun titulaire" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun</SelectItem>
                        {teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground mt-1">Professeur responsable de cette classe (bulletin, discipline, communication parents).</p>
                  </div>
                  {currentYear && <div className="text-[11px] text-muted-foreground">Rattachée à l'année <b>{currentYear.name}</b>.</div>}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setOpenClass(false); setEditingClass(null); }}>Annuler</Button>
                  <Button onClick={saveClass} data-testid="btn-save-class">{editingClass ? "Enregistrer" : "Créer"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {classes.map(c => (
              <Card key={c.id} className={`p-5 card-hover ${c.status === "archived" ? "opacity-60 border-dashed" : ""}`} data-testid={`card-class-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center"><Chalkboard size={20} weight="duotone" /></div>
                  <div className="flex gap-1">
                    {canEdit && c.status !== "archived" && (
                      <Button variant="ghost" size="icon" onClick={() => openEditClass(c)} data-testid={`btn-edit-class-${c.id}`} title="Modifier">
                        <PencilSimple size={16} className="text-primary" />
                      </Button>
                    )}
                    {c.status === "archived" ? (
                      <Button variant="ghost" size="sm" onClick={() => restoreClass(c.id)} data-testid={`btn-restore-class-${c.id}`}>Restaurer</Button>
                    ) : (
                      canEdit && <Button variant="ghost" size="icon" onClick={() => delClass(c.id)} data-testid={`btn-del-class-${c.id}`}><Trash size={16} className="text-destructive" /></Button>
                    )}
                  </div>
                </div>
                <div className="mt-3 font-heading text-lg font-semibold flex items-center gap-2">
                  {c.name}
                  {c.status === "archived" && <span className="text-[10px] uppercase bg-muted px-1.5 py-0.5 rounded font-mono">Archivée</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {gradeLevels.find(g => g.id === c.grade_level_id)?.name || c.level}
                  {c.stream || c.series ? ` · ${c.stream || c.series}` : ""} · {c.capacity} places
                </div>
                {(c.main_teacher_id || c.class_teacher_id) && (
                  <div className="text-[11px] text-primary mt-1 font-medium">
                    Titulaire : {teachers.find(t => t.id === (c.main_teacher_id || c.class_teacher_id))?.name || "—"}
                  </div>
                )}
              </Card>
            ))}
            {!classes.length && <div className="col-span-full text-center text-sm text-muted-foreground py-12">Aucune classe. Créez la première.</div>}
          </div>
        </TabsContent>

        <TabsContent value="subjects" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={openSubject} onOpenChange={(v) => { setOpenSubject(v); if (!v) setEditingSubject(null); }}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-subject"
                        onClick={() => { setEditingSubject(null); setSubForm({ name: "", code: "", coefficient: 1, class_id: "" }); }}>
                  <Plus size={16} className="mr-2" /> Nouvelle matière
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{editingSubject ? `Modifier ${editingSubject.name}` : "Nouvelle matière"}</DialogTitle></DialogHeader>
                <div className="grid gap-4">
                  <div><Label>Nom</Label><Input value={subForm.name} onChange={e => setSubForm({ ...subForm, name: e.target.value })} data-testid="input-subject-name" /></div>
                  <div><Label>Code</Label><Input value={subForm.code} onChange={e => setSubForm({ ...subForm, code: e.target.value })} data-testid="input-subject-code" /></div>
                  <div><Label>Coefficient</Label><Input type="number" step="0.5" value={subForm.coefficient} onChange={e => setSubForm({ ...subForm, coefficient: e.target.value })} data-testid="input-subject-coef" /></div>
                  <div>
                    <Label>Classe (optionnel)</Label>
                    <Select value={subForm.class_id || "none"} onValueChange={v => setSubForm({ ...subForm, class_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="select-subject-class"><SelectValue placeholder="Aucune" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucune</SelectItem>
                        {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setOpenSubject(false); setEditingSubject(null); }}>Annuler</Button>
                  <Button onClick={saveSubject} data-testid="btn-save-subject">{editingSubject ? "Enregistrer" : "Créer"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Matière</th><th className="px-4 py-3">Code</th><th className="px-4 py-3">Coef.</th><th className="px-4 py-3">Classe</th><th className="px-4 py-3 text-right"></th>
              </tr></thead>
              <tbody>
                {subjects.map((s, i) => (
                  <tr key={s.id} className={`border-b border-border last:border-0 ${i % 2 ? "bg-muted/30" : ""} ${s.status === "archived" ? "opacity-60" : ""}`}>
                    <td className="px-4 py-3 font-medium flex items-center gap-2">
                      <BookOpen size={16} weight="duotone" className="text-secondary" /> {s.name}
                      {s.status === "archived" && <span className="text-[9px] uppercase bg-muted px-1 py-0.5 rounded font-mono">Archivée</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{s.code}</td>
                    <td className="px-4 py-3 tabular-nums">{s.coefficient}</td>
                    <td className="px-4 py-3">{classById[s.class_id]?.name || <span className="text-muted-foreground italic">toutes</span>}</td>
                    <td className="px-4 py-3 text-right">
                      {s.status === "archived" ? (
                        <Button variant="ghost" size="sm" onClick={() => restoreSubject(s.id)} data-testid={`btn-restore-subject-${s.id}`}>Restaurer</Button>
                      ) : (
                        <div className="inline-flex gap-1">
                          {canEdit && (
                            <Button variant="ghost" size="icon" onClick={() => openEditSubject(s)} data-testid={`btn-edit-subject-${s.id}`} title="Modifier">
                              <PencilSimple size={16} className="text-primary" />
                            </Button>
                          )}
                          {canEdit && (
                            <Button variant="ghost" size="icon" onClick={() => delSubject(s.id)} data-testid={`btn-del-subject-${s.id}`}>
                              <Trash size={16} className="text-destructive" />
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {!subjects.length && <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-muted-foreground">Aucune matière.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
